import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaMoneyBillWave, FaCreditCard, FaSave, FaRedo } from 'react-icons/fa';
import { MdDescription, MdAttachMoney } from 'react-icons/md';
import { ENDPOINT } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import './Spending.css';

// Configure axios-retry for this component's API calls
const axiosInstance = axios.create();

const banks = [
  { value: 'CBE', label: 'CBE' },
  { value: 'Awash', label: 'Awash' },
  { value: 'Dashen', label: 'Dashen' },
  { value: 'Abyssinia', label: 'Abyssinia' },
  { value: 'Birhan', label: 'Birhan' },
  { value: 'Telebirr', label: 'Telebirr' },
];

const categories = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'consumption', label: 'Consumption' },
];

const Spending = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    amount: '',
    category: 'purchase',
    reason: '',
    comment: '',
    paymentMethod: 'cash',
    bankName: banks[0].value,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!formData.reason.trim()) {
      toast.error('Please enter a reason for the spending');
      return;
    }

    setError(null);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const spendingData = {
        user_id: user?.id,
        amount: parseFloat(formData.amount),
        category: formData.category,
        reason: formData.reason,
        comment: formData.comment,
        payment_method: formData.paymentMethod,
        bank_name: formData.paymentMethod === 'bank' ? formData.bankName : null
      };

      const response = await axios.post(
        ENDPOINT,
        { ...spendingData, action: 'add_spending' },
        { headers }
      );

      const responseData = response.data;

      if (responseData.success) {
        // Clear form
        setFormData({
          amount: '',
          category: 'purchase',
          reason: '',
          comment: '',
          paymentMethod: 'cash',
          bankName: banks[0].value,
        });
        toast.success('Spending recorded successfully!');
        setRetryCount(0);
      } else {
        setError(responseData.message || 'Failed to record spending');
        toast.error(responseData.message || 'Failed to record spending');
      }
    } catch (err) {
      console.error("Error recording spending:", err);
      
      if (retryCount < 3) {
        // Auto-retry with exponential backoff
        const delay = (retryCount + 1) * 1000;
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          handleSubmit(e);
        }, delay);
        return;
      }
      
      const message = err.response?.data?.message || err.message || 'An unexpected error occurred';
      setError(message);
      toast.error('Error: ' + message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="spending-container">
      <div className="spending-card">
        <h2 className="spending-title">Record Spending</h2>
        
        {error && (
          <div className="error-message">
            <p>{error}</p>
            <button 
              className="retry-button"
              onClick={handleSubmit}
              disabled={loading}
            >
              <FaRedo className="retry-icon" /> Retry
            </button>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="spending-form">
          <div className="form-group">
            <label htmlFor="amount">Amount</label>
            <div className="input-with-icon">
              <MdAttachMoney className="input-icon" />
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                step="0.01"
                min="0.01"
                required
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="form-control"
              disabled={loading}
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label>Payment Method</label>
            <div className="payment-methods">
              <button
                type="button"
                className={`method-button ${formData.paymentMethod === 'cash' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'cash' }))}
                disabled={loading}
              >
                <FaMoneyBillWave className="method-icon" />
                <span>Cash</span>
              </button>
              
              <button
                type="button"
                className={`method-button ${formData.paymentMethod === 'bank' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'bank' }))}
                disabled={loading}
              >
                <FaCreditCard className="method-icon" />
                <span>Bank</span>
              </button>
            </div>
          </div>
          
          {formData.paymentMethod === 'bank' && (
            <div className="form-group">
              <label>Select Bank</label>
              <div className="bank-buttons">
                {banks.map(bank => (
                  <button
                    key={bank.value}
                    type="button"
                    className={`bank-button ${formData.bankName === bank.value ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, bankName: bank.value }))}
                    disabled={loading}
                  >
                    {bank.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="reason">Reason</label>
            <div className="input-with-icon">
              <MdDescription className="input-icon" />
              <input
                type="text"
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Enter reason for spending"
                required
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="comment">Comment (Optional)</label>
            <textarea
              id="comment"
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              placeholder="Add any additional comments"
              rows="3"
              disabled={loading}
              className="form-control"
            />
          </div>
          
          <button
            type="submit"
            className="submit-button"
            disabled={loading || !formData.amount || parseFloat(formData.amount) <= 0 || !formData.reason.trim()}
          >
            {loading ? (
              <span className="button-loading">
                <span className="spinner"></span>
                Recording...
              </span>
            ) : (
              <>
                <FaSave className="button-icon" />
                Record Spending
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Spending;
